// Standard MIDI File (SMF) chunk reader and writer.

export interface SmfEvent {
  /** Absolute tick. */
  tick: number;
  /** Raw message bytes including the status byte (meta: 0xFF, type, …data; sysex: 0xF0/0xF7, …data). */
  data: number[];
  /** Sort priority at equal ticks (lower first). */
  order?: number;
}

export interface SmfFile {
  format: number;
  /** Ticks per quarter note (SMPTE divisions are converted assuming 120 bpm). */
  ppq: number;
  tracks: ParsedEvent[][];
}

export type ParsedEvent =
  | { tick: number; kind: 'meta'; type: number; data: Uint8Array }
  | { tick: number; kind: 'sysex'; data: Uint8Array }
  | { tick: number; kind: 'channel'; status: number; channel: number; d1: number; d2: number };

function varLen(n: number): number[] {
  let v = Math.max(0, Math.floor(n)) >>> 0;
  const bytes = [v & 0x7f];
  v >>>= 7;
  while (v > 0) {
    bytes.unshift((v & 0x7f) | 0x80);
    v >>>= 7;
  }
  return bytes;
}

function u32(n: number): number[] {
  return [(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff];
}

/** Serialize tracks of absolute-tick events into an SMF (an End of Track event is appended when missing). */
export function writeSmf(format: 0 | 1, ppq: number, tracks: SmfEvent[][]): Uint8Array {
  const out: number[] = [];
  out.push(0x4d, 0x54, 0x68, 0x64, ...u32(6), 0, format, (tracks.length >> 8) & 0xff, tracks.length & 0xff, (ppq >> 8) & 0x7f, ppq & 0xff);
  for (const events of tracks) {
    const sorted = events
      .map((e, i) => ({ e, i }))
      .sort((a, b) => a.e.tick - b.e.tick || (a.e.order ?? 5) - (b.e.order ?? 5) || a.i - b.i)
      .map((x) => x.e);
    const body: number[] = [];
    let last = 0;
    let hasEot = false;
    for (const e of sorted) {
      if (hasEot) break;
      const t = Math.max(last, Math.round(e.tick));
      body.push(...varLen(t - last));
      last = t;
      const [status, ...rest] = e.data;
      if (status === 0xff) {
        const [type, ...data] = rest;
        body.push(0xff, type, ...varLen(data.length), ...data);
        if (type === 0x2f) hasEot = true;
      } else if (status === 0xf0 || status === 0xf7) {
        body.push(status, ...varLen(rest.length), ...rest);
      } else body.push(status, ...rest);
    }
    if (!hasEot) body.push(0, 0xff, 0x2f, 0);
    out.push(0x4d, 0x54, 0x72, 0x6b, ...u32(body.length), ...body);
  }
  return new Uint8Array(out);
}

/** Parse an SMF. Truncated tracks are read up to the damage. */
export function readSmf(bytes: Uint8Array): SmfFile {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const id = (p: number) => String.fromCharCode(bytes[p], bytes[p + 1], bytes[p + 2], bytes[p + 3]);
  let p = 0;
  // RIFF-wrapped MIDI (.rmi).
  if (bytes.length >= 20 && id(0) === 'RIFF' && id(8) === 'RMID') {
    let q = 12;
    while (q + 8 <= bytes.length) {
      const len = dv.getUint32(q + 4, true);
      if (id(q) === 'data') return readSmf(bytes.subarray(q + 8, q + 8 + len));
      q += 8 + len + (len & 1);
    }
  }
  if (bytes.length < 14 || id(0) !== 'MThd') throw new Error('Not a Standard MIDI File (missing MThd header)');
  const hlen = dv.getUint32(4);
  const format = dv.getUint16(8);
  const ntrks = dv.getUint16(10);
  const division = dv.getUint16(12);
  let ppq: number;
  if (division & 0x8000) {
    const fps = 256 - (division >> 8);
    const tpf = division & 0xff;
    ppq = Math.max(1, Math.round((fps * tpf) / 2));
  } else ppq = division || 480;
  p = 8 + hlen;
  const tracks: ParsedEvent[][] = [];
  while (p + 8 <= bytes.length && tracks.length < Math.max(ntrks, 1) + 64) {
    const cid = id(p);
    const len = dv.getUint32(p + 4);
    const start = p + 8;
    const end = Math.min(bytes.length, start + len);
    p = start + len;
    if (cid !== 'MTrk') continue;
    tracks.push(parseTrack(bytes, start, end));
  }
  return { format, ppq, tracks };
}

function parseTrack(bytes: Uint8Array, start: number, end: number): ParsedEvent[] {
  const out: ParsedEvent[] = [];
  let p = start;
  let tick = 0;
  let running = 0;
  const readVar = (): number => {
    let v = 0;
    for (let k = 0; k < 4; k++) {
      if (p >= end) throw new RangeError('truncated');
      const b = bytes[p++];
      v = (v << 7) | (b & 0x7f);
      if (!(b & 0x80)) return v;
    }
    return v;
  };
  try {
    while (p < end) {
      tick += readVar();
      if (p >= end) break;
      let status = bytes[p];
      if (status & 0x80) p++;
      else if (running) status = running;
      else throw new RangeError('data byte without status');
      if (status === 0xff) {
        const type = bytes[p++];
        const len = readVar();
        const data = bytes.subarray(p, Math.min(end, p + len));
        p += len;
        out.push({ tick, kind: 'meta', type, data });
        if (type === 0x2f) break;
      } else if (status === 0xf0 || status === 0xf7) {
        const len = readVar();
        out.push({ tick, kind: 'sysex', data: bytes.subarray(p, Math.min(end, p + len)) });
        p += len;
      } else if (status >= 0x80 && status < 0xf0) {
        running = status;
        const hi = status & 0xf0;
        const d1 = bytes[p++] ?? 0;
        const d2 = hi === 0xc0 || hi === 0xd0 ? 0 : bytes[p++] ?? 0;
        if (p > end) break;
        out.push({ tick, kind: 'channel', status: hi, channel: status & 0x0f, d1: d1 & 0x7f, d2: d2 & 0x7f });
      } else {
        // System common/real-time messages inside a file: skip their data bytes.
        const lens: Record<number, number> = { 0xf1: 1, 0xf2: 2, 0xf3: 1 };
        p += lens[status] ?? 0;
      }
    }
  } catch {
    // Truncated or corrupt track: keep what was read.
  }
  return out;
}

// ---- event builders

export const meta = (tick: number, type: number, data: number[], order = 0): SmfEvent => ({ tick, data: [0xff, type, ...data], order });

export function metaText(tick: number, type: number, text: string, order = 0): SmfEvent {
  return meta(tick, type, [...new TextEncoder().encode(text)], order);
}
